import angular from 'angular'
import VendNumber from 'vend-number'
import { ApiCustomer, ApiProduct } from '@vend/types'

import ngRedux from 'ng-redux'
import { trackError } from '@vend/utilities'
import type { VdToastNotificationService } from '../vend-ui/types'
import { STATES } from '../../../lib/routes'
import { ProductsStore } from '../factories/database/products-store'
import { $log, $q, $state } from '../ng-compat/imports'
import {
  KLARNA_EXCHANGE_NOT_POSSIBLE_MESSAGE,
  shouldPreventExchangeForKlarna,
} from '../functions/payment-types/klarna'
import { CustomersStore } from '../factories/database/customers-store'
import { isConfirmedNotReadyToComplete } from '../functions/sale'
import { RootState } from '../state/store'
import type { ProductSkuIndex } from '../factories/search/product-sku-index'
import { addUnknownLineItem } from '../state/slices/currentSale/currentSaleSlice'
import { ApiEvent, clientApiEventBus } from './client-api-event-bus'
import { RegisterManager } from './register-manager'
import { SaleManager } from './sale-manager'
import { SyncManager } from './sync-manager'
import { WebRegisterStatusManager } from './web-register-status-manager'

/**
 * Responsible for the client API.
 *
 * @module clientApiManager
 * @class ClientApiManager
 * @constructor
 */
export class ClientApiManager {
  private _whenInitialised: $TSFixMe
  private _whenReadyForProductEvents: $TSFixMe

  constructor(
    private productsStore: ProductsStore,
    private productSkuIndex: ProductSkuIndex,
    private saleManager: SaleManager,
    private registerManager: RegisterManager,
    private syncManager: SyncManager,
    private webRegisterStatusManager: WebRegisterStatusManager,
    private vdToastNotificationService: VdToastNotificationService,
    private customersStore: CustomersStore,
    private readonly $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'
  }

  // ============================== Public API ============================== //

  /**
   * Initialise the Client API Manager.
   *
   * @method init
   *
   * @return {Promise} A promise resolved once initialisation is complete
   */
  public init() {
    if (!this._whenInitialised) {
      clientApiEventBus.init()
      clientApiEventBus.on('apiEvent', this._onApiEvent, this)
      this._whenInitialised = $q.all([
        this.registerManager.init(),
        this.saleManager.init(),
      ])

      this._whenReadyForProductEvents = $q.all([
        this._whenInitialised,
        this.productSkuIndex.whenSyncComplete(),
      ])
    }

    return this._whenInitialised
  }

  /**
   * Parses the legacy 'product-add' params, and invokes the add product handler with the appropriate params.
   *
   * @method addLegacyProduct
   *
   * @param {Array} params
   *        The legacy 'product-add' params in array format.
   *
   * @return {Promise} A promise resolved if the client API invocation is accepted and processed successfully,
   *         rejected if there is an error
   */
  public addLegacyProduct(params: $TSFixMe[]) {
    const [product_id, quantity, unit_price, statusParam] = params
    let confirmed = false

    if (
      statusParam &&
      statusParam.status &&
      statusParam.status === 'CONFIRMED'
    ) {
      confirmed = true
    }

    return this.addProduct({ product_id, quantity, unit_price, confirmed })
  }

  /**
   * Adds a product to the sale with the given product ID, quantity and base price.
   *
   * @method addProduct
   *
   * @param {Object} params
   *        @param [params.product_id] {String}
   *               The id of the product to add.
   *
   *        @param [params.sku] {String}
   *               The sku of the product to add.
   *
   *        @param [params.quantity] {String}
   *               The quantity to set on the added product.
   *
   *        @param [params.unit_price] {String}
   *               The unit price to set on the added product.
   *
   *        @param [params.note] {String}
   *               The note to set on the added product.
   *
   *        @param [params.confirmed=false] {Boolean}
   *               Sets the added product as confirmed.
   *
   * @return {Promise} A promise resolved if the client API invocation is accepted and processed successfully,
   *         rejected if there is an error
   */
  public addProduct(params: {
    product_id?: string
    sku?: string
    quantity?: string
    unit_price?: string
    note?: string
    confirmed?: boolean
  }) {
    return this._whenReadyForProductEvents
      .then(() => this._ensureReadyToSell())
      .then(() =>
        $q((resolve, reject) => {
          const productId = params.product_id
          const sku = params.sku
          const quantity = params.quantity
          const price = params.unit_price
          const note = params.note
          const confirmed = params.confirmed

          if (!productId && !sku) {
            return reject(new Error('No product ID or SKU provided.'))
          }

          if (quantity && !VendNumber.isFinite(quantity)) {
            return reject(new Error('Please provide a valid quantity.'))
          }

          if (price && !VendNumber.isFinite(price)) {
            return reject(new Error('Please provide a valid unit price.'))
          }

          if (productId) {
            this.productsStore
              .get(productId)
              .then(product => {
                if (!product) {
                  return reject(
                    new Error('Cannot find a product with the provided ID.')
                  )
                }

                if (!product.active) {
                  return reject(
                    new Error('The product with provided ID is not active.')
                  )
                }

                this._addProduct({
                  product,
                  quantity,
                  price,
                  note,
                  confirmed,
                }).then(resolve, reject)
              })
              .catch(reject)
          } else if (sku) {
            this.productSkuIndex
              .search(sku)
              .then((results: $TSFixMe) => {
                if (!results || !results.length) {
                  return reject(
                    new Error('Cannot find a product with the provided SKU.')
                  )
                }

                if (results.length > 1) {
                  return reject(
                    new Error(
                      'There are multiple products with the provided SKU.'
                    )
                  )
                }

                const product = results[0].product
                this._addProduct({
                  product,
                  quantity,
                  price,
                  note,
                  confirmed,
                }).then(resolve, reject)
              })
              .catch(reject)
          }
        })
      )
  }

  public addUnknownProduct(params: { name: string }) {
    return this._whenReadyForProductEvents
      .then(() => this._ensureReadyToSell())
      .then(() => {
        const { name } = params
        if (!name) {
          return $q.reject(new Error('No product name provided.'))
        }
        this.$ngRedux.dispatch(addUnknownLineItem(name))
      })
  }

  /**
   * Parses the legacy 'customer-add' params, and invokes the add customer handler with the appropriate params.
   *
   * @method addLegacyCustomer
   *
   * @param {Array} params
   *        The legacy 'customer-add' params in array format.
   *
   * @return {Promise} A promise resolved if the client API invocation is accepted and processed successfully,
   *         rejected if there is an error
   */
  public addLegacyCustomer(params: $TSFixMe[]) {
    return this.addCustomer({ customer_id: params[0] })
  }

  /**
   * Sets the customer of the sale to the customer with the passed customer ID.
   *
   * @method addCustomer
   *
   * @param {Object} params
   *        @param params.customer_id {String}
   *               The id of the customer to add.
   *
   * @return {Promise} A promise resolved if the client API invocation is accepted and processed successfully,
   *         rejected if there is an error
   */
  public addCustomer(params: { customer_id: string }) {
    return this._whenInitialised
      .then(() => this._ensureReadyToSell())
      .then(() =>
        $q((resolve, reject) => {
          const customerId = params.customer_id

          const sale = this.$ngRedux.getState<RootState>().currentSale
          if (isConfirmedNotReadyToComplete(sale)) {
            return reject(new Error('The customer can not be changed.'))
          }

          if (!customerId) {
            return reject(new Error('No customer ID provided.'))
          }
          this.customersStore.get(customerId).then((customer?: ApiCustomer) => {
            if (!customer) {
              return reject(new Error(`Cannot find customer with ID`))
            }

            $log.debug(
              '[Client API Manager] Adding customer to sale.',
              customer
            )
            this.saleManager.addCustomer(customer)
            resolve()
          })
        })
      )
  }

  // ============================== Private API ============================== //

  /**
   * Handle an API event recieved by the Client API Event Bus.
   *
   * @private
   * @method _onApiEvent
   *
   * @param  {Object} apiEvent
   *
   *         @param apiEvent.method {String}
   *                The Client API method to invoke in response to the event
   *
   *         @param apiEvent.params {Object}
   *                The parameters to pass to the Client API method
   *
   *         @param apiEvent.event {Object}
   *                The event that triggered the API call
   */
  private _onApiEvent(apiEvent: ApiEvent) {
    const { method, params, event } = apiEvent
    this[method as keyof ClientApiManager](params).catch((error: Error) =>
      clientApiEventBus.logError(event, error.message)
    )
  }

  /**
   * Ensure we are on the Sell screen and the register is open and all synced data is current
   *
   * @private
   * @method _ensureReadyToSell
   *
   * @return {Promise} A promise resolved if we are on the sell screen with an open register and synced the latest data,
   *         rejected otherwise
   */
  private _ensureReadyToSell() {
    return $q((resolve, reject) => {
      if (!this.registerManager.isActiveRegisterOpen()) {
        return reject(new Error('The register is not open.'))
      }

      // We only support post message when we are in the current sale state.
      if (!$state.is(STATES.SALE)) {
        return reject(
          new Error('Unable to process API events on the current page.')
        )
      }

      return resolve()
    }).then(() => {
      // We only sync to ensure latest product data is available in the products store. However if we're offline the
      // sync will fail resulting in the product not being added to the sale, even if the product does actually exist
      // in the store.
      if (this.webRegisterStatusManager.isOnline()) {
        return this.syncManager.sync({ skipEmitSyncComplete: true })
      }
    })
  }

  /**
   * Adds a line item to the sale with the given product and optionally sets the quantity, price, note or confirmed
   * status to the given values if provided.
   *
   * @private
   * @method _addProduct
   *
   * @param {Object} params
   *        @param [params.product] {String}
   *               The product to add to the sale.
   *
   *        @param [params.quantity] {String}
   *               The quantity to set on the added product.
   *
   *        @param [params.price] {String}
   *               The unit price to set on the added product.
   *
   *        @param [params.note] {String}
   *               The note to add to the line item for the added product.
   *
   *        @param [params.confirmed=false] {Boolean}
   *               Sets the added product's line item as confirmed.
   *
   * @return {Promise} Resolved once the product has been added to the sale.
   */
  private _addProduct({
    product,
    quantity,
    price,
    note,
    confirmed,
  }: {
    product: ApiProduct
    quantity?: string
    price?: string
    note?: string
    confirmed?: boolean
  }) {
    const sale = this.$ngRedux.getState<RootState>().currentSale
    if (shouldPreventExchangeForKlarna(sale)) {
      this.vdToastNotificationService.negative({
        message: KLARNA_EXCHANGE_NOT_POSSIBLE_MESSAGE,
      })
      return $q.reject(
        new Error(
          'Unable to add product to the return sale. The original sale had a Klarna payment'
        )
      )
    }

    const data = { product, quantity, price, note, confirmed }
    $log.debug('[Client API Manager] Adding product to sale.', data)
    return this.saleManager
      .addProductToSale(product, { quantity, price, note, confirmed })
      .then(() => this.$ngRedux.getState<RootState>().currentSale)
      .catch((error: Error) => {
        trackError(`[Client API] Unexpected error adding product to sale`, {
          data: { error, data },
        })

        return $q.reject(
          new Error('Unexpected error occurred adding product to sale')
        )
      })
  }
}

angular
  .module('webregister.managers')
  .service('clientApiManager', ClientApiManager)
